import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';

import { } from '@lockerpm/design';

import { PlusOutlined } from "@ant-design/icons";

import itemsComponents from "../../../components/items";
import commonComponents from "../../../components/common";

import Filter from "./components/members/Filter";
import TableData from "./components/members/TableData";
import BoxData from "./components/members/BoxData";
import FormData from "./components/members/FormData";

import global from "../../../config/global";
import projectServices from "../../../secrets-web/src/services/project";
import coreServices from "../../../secrets-web/src/services/core";
import environmentServices from "../../../secrets-web/src/services/environment";
import { CipherType } from "../../../secrets-web/src/core-js/src/enums";
import { getRouterByLocation, scrollEnd } from '../../../secrets-web/src/utils/common';

const Members = () => {
  const { PageHeader } = commonComponents;
  const { NoData, Pagination } = itemsComponents;

  const { t } = useTranslation()

  const isMobile = useSelector((state) => state.system.isMobile)
  const locale = useSelector((state) => state.system.locale)

  const currentPage = getRouterByLocation(window.location);
  const projectId = currentPage?.params?.project_id;

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [data, setData] = useState([]);
  const [params, setParams] = useState({
    role: '',
    status: '',
    page: 1,
    size: global.constants.PAGE_SIZE,
  });

  useEffect(() => {
    if (projectId) {
      fetchEnvironments()
    }
  }, [projectId])

  useEffect(() => {
    if (projectId) {
      fetchData()
    }
  }, [projectId, JSON.stringify(params)])

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      size: global.constants.PAGE_SIZE
    })
  }, [isMobile])

  const handleChangePage = (page, size) => {
    setParams({
      ...params,
      page,
      size
    })
  };

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const fetchEnvironments = async () => {
    await environmentServices.list(projectId, { paging: 0 }).then(async (response) => {
      const environments = await coreServices.decrypt_ciphers(response.results, CipherType.Environment)
      setEnvironments(environments);
    }).catch(() => {
      setEnvironments([]);
    })
  }

  const fetchData = () => {
    setLoading(true)
    projectServices.members(currentPage.params?.project_id, params).then((response) => {
      setData(response.results);
      setTotal(response.count)
      setLoading(false)
    }).catch(() => {
      setData([])
      setTotal(0)
      setLoading(false)
    })
  }

  const handleDelete = (member) => {
    global.confirm(() => {
      projectServices.remove_member(currentPage.params?.project_id, member.id).then(async () => {
        global.pushSuccess(t('notification.success.member.deleted'));
        if (data.length === 1 && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        }
        fetchData();
      }).catch((error) => {
        global.pushError(error)
      });
    });
  };

  return (
    <div
      className="members layout-content"
      onScroll={(e) => scrollEnd(e, params, total, setParams)}
    >
      <PageHeader
        title={t('project_members.title')}
        subtitle={t('project_members.description')}
        docLabel={t('project_members.doc')}
        docLink={global.urls.DOC_LINKS[locale].PROJECT_MEMBERS}
        actions={[
          {
            key: 'add',
            label: t('button.new_member'),
            type: 'primary',
            icon: <PlusOutlined />,
            click: () => handleOpenForm()
          }
        ]}
      />
      {
        <Filter
          className={'mt-6'}
          params={params}
          setParams={setParams}
        />
      }
      {
        total == 0 && <NoData
          loading={loading}
          className={'mt-6'}
        />
      }
      {
        total > 0 && <>
          {
            isMobile ? <BoxData
              className="mt-4"
              loading={loading}
              data={data}
              params={params}
              environments={environments}
              onUpdate={handleOpenForm}
              onDelete={handleDelete}
            /> : <TableData
              className="mt-4"
              loading={loading}
              data={data}
              params={params}
              environments={environments}
              onUpdate={handleOpenForm}
              onDelete={handleDelete}
            />
          }
        </>
      }
      {
        total > params.size && !isMobile && <Pagination
          params={params}
          total={total}
          onChange={handleChangePage}
        />
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        environments={environments}
        onClose={() => setFormVisible(false)}
        onReload={() => fetchData()}
      />
    </div>
  );
}

export default Members;